import { useState, useEffect, useMemo } from "react";
import { ClassDetail } from "./ClassDetail.tsx";
import { cache, onCacheChange } from "./useSWR.ts";
import { formatTime, formatDateFi, todayStr, shiftDate } from "./utils.ts";
import type { ClassEvent } from "./classCategory.ts";

const COLLAPSED_KEY = "parkourakatemia:panelCollapsed";

function getMondayOfWeek(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getClassesForDate(date: string): ClassEvent[] {
  return (cache.get(`classes/${date}`)?.data as ClassEvent[] | undefined) || [];
}

function getWeekDates(date: string): string[] {
  const monday = getMondayOfWeek(date);
  return Array.from({ length: 7 }, (_, i) => shiftDate(monday, i));
}

interface DayStats {
  date: string;
  classes: ClassEvent[];
  totalClasses: number;
  totalEnrolled: number;
  totalCapacity: number;
}

function computeDayStats(date: string, classes: ClassEvent[]): DayStats {
  let totalEnrolled = 0;
  let totalCapacity = 0;
  for (const cls of classes) {
    totalEnrolled += cls.customer?.length || 0;
    totalCapacity += cls.maxparticipants || 0;
  }
  return { date, classes, totalClasses: classes.length, totalEnrolled, totalCapacity };
}

const WEEKDAY_NAMES = ["su", "ma", "ti", "ke", "to", "pe", "la"];

export function PanelStats({ date, viewMode }: { date: string; viewMode: "day" | "week" }) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    // Re-render when cache updates (e.g. initial load, revalidation)
    const unsub = onCacheChange(() => setTick((t) => t + 1));
    // Also tick every 30s so "next class" stays current
    const iv = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => { unsub(); clearInterval(iv); };
  }, []);

  const isWeek = viewMode === "week";

  const stats = useMemo(() => {
    const dates = isWeek ? getWeekDates(date) : [date];
    const dayStatsList = dates.map((d) => computeDayStats(d, getClassesForDate(d)));

    let totalClasses = 0;
    let totalEnrolled = 0;
    let totalCapacity = 0;
    const gymMap = new Map<string, { count: number; enrolled: number; capacity: number }>();

    for (const day of dayStatsList) {
      totalClasses += day.totalClasses;
      totalEnrolled += day.totalEnrolled;
      totalCapacity += day.totalCapacity;

      for (const cls of day.classes) {
        const gym = cls.employee_names?.[0]?.service_provider_name || "Muu";
        const entry = gymMap.get(gym) || { count: 0, enrolled: 0, capacity: 0 };
        entry.count++;
        entry.enrolled += cls.customer?.length || 0;
        entry.capacity += cls.maxparticipants || 0;
        gymMap.set(gym, entry);
      }
    }

    // Fill percentage
    const fillPct = totalCapacity > 0 ? Math.round((totalEnrolled / totalCapacity) * 100) : null;

    // Next upcoming class (today only)
    const today = todayStr();
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    let nextClass: ClassEvent | null = null;
    const todayClasses = getClassesForDate(today);
    for (const cls of todayClasses) {
      const h = parseInt(cls.start.slice(11, 13), 10);
      const m = parseInt(cls.start.slice(14, 16), 10);
      const startMin = h * 60 + m;
      if (startMin > nowMinutes) {
        if (!nextClass) {
          nextClass = cls;
        } else {
          const nh = parseInt(nextClass.start.slice(11, 13), 10);
          const nm = parseInt(nextClass.start.slice(14, 16), 10);
          if (startMin < nh * 60 + nm) nextClass = cls;
        }
      }
    }

    const gyms = Array.from(gymMap.entries()).sort((a, b) => a[0].localeCompare(b[0], "fi"));

    // Busiest day (week mode)
    let busiestDay: DayStats | null = null;
    if (isWeek) {
      for (const day of dayStatsList) {
        if (day.totalClasses > 0 && (!busiestDay || day.totalEnrolled > busiestDay.totalEnrolled)) {
          busiestDay = day;
        }
      }
    }

    // Per-day breakdown for week
    const dayBreakdown = isWeek ? dayStatsList.filter((d) => d.totalClasses > 0) : [];

    return { totalClasses, totalEnrolled, totalCapacity, fillPct, gyms, nextClass, busiestDay, dayBreakdown };
  }, [date, isWeek, tick]);

  if (stats.totalClasses === 0) {
    return (
      <div className="panel-stats">
        <div className="panel-stats-title">Yhteenveto</div>
        <div className="panel-stats-empty">Ei tunteja</div>
      </div>
    );
  }

  const periodLabel = isWeek ? "viikolla" : (date === todayStr() ? "tänään" : formatDateFi(date));

  return (
    <div className="panel-stats">
      <div className="panel-stats-title">Yhteenveto</div>

      <div className="stat-cards">
        <div className="stat-card">
          <div className="stat-value">{stats.totalClasses}</div>
          <div className="stat-label">Tunteja {periodLabel}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {stats.totalEnrolled}
            {stats.totalCapacity > 0 && <span className="stat-capacity"> / {stats.totalCapacity}</span>}
          </div>
          <div className="stat-label">Osallistujia</div>
        </div>
      </div>

      {stats.fillPct !== null && (
        <div className="stat-card stat-fill-card">
          <div className="stat-fill-row">
            <div>
              <div className="stat-value stat-fill-value">{stats.fillPct}%</div>
              <div className="stat-label">Täyttöaste</div>
            </div>
            <div className="stat-fill-bar-container">
              <div className="stat-fill-bar">
                <div
                  className={`stat-fill-bar-inner ${stats.fillPct >= 90 ? "fill-full" : stats.fillPct >= 70 ? "fill-almost" : "fill-ok"}`}
                  style={{ width: `${Math.min(stats.fillPct, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {stats.nextClass && (
        <div className="stat-next">
          <div className="stat-label">Seuraava tunti</div>
          <div className="stat-next-name">{stats.nextClass.productname?.trim()}</div>
          <div className="stat-next-time">
            {formatTime(stats.nextClass.start)} – {formatTime(stats.nextClass.end)}
          </div>
        </div>
      )}

      {stats.busiestDay && (
        <div className="stat-next">
          <div className="stat-label">Vilkkain päivä</div>
          <div className="stat-next-name">{formatDateFi(stats.busiestDay.date)}</div>
          <div className="stat-next-time">
            {stats.busiestDay.totalClasses} tuntia, {stats.busiestDay.totalEnrolled} hlö
          </div>
        </div>
      )}

      {isWeek && stats.dayBreakdown.length > 0 && (
        <div className="stat-gyms">
          <div className="stat-label">Päivät</div>
          {stats.dayBreakdown.map((day) => {
            const d = new Date(day.date + "T12:00:00");
            const dayName = WEEKDAY_NAMES[d.getDay()];
            const dayNum = d.getDate();
            return (
              <div key={day.date} className="stat-gym-row">
                <span className="stat-gym-name">{dayName} {dayNum}.</span>
                <span className="stat-gym-count">
                  {day.totalClasses} tuntia, {day.totalEnrolled} hlö
                  {day.totalCapacity > 0 && ` (${Math.round((day.totalEnrolled / day.totalCapacity) * 100)}%)`}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="stat-gyms">
        <div className="stat-label">Salit</div>
        {stats.gyms.map(([gym, data]) => (
          <div key={gym} className="stat-gym-row">
            <span className="stat-gym-name">{gym}</span>
            <span className="stat-gym-count">
              {data.count} tuntia, {data.enrolled} hlö
              {data.capacity > 0 && ` (${Math.round((data.enrolled / data.capacity) * 100)}%)`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SidePanel({
  selectedClassId,
  onDismiss,
  onSessionExpired,
  date,
  viewMode,
}: {
  selectedClassId: string | null;
  onDismiss: () => void;
  onSessionExpired: () => void;
  date: string;
  viewMode: "day" | "week";
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Auto-expand when a class is selected
  useEffect(() => {
    if (selectedClassId) setCollapsed(false);
  }, [selectedClassId]);

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, String(next));
      return next;
    });
  };

  return (
    <div className={`wide-detail-panel${collapsed ? " panel-collapsed" : ""}`}>
      <div className="panel-header">
        {!collapsed && selectedClassId && (
          <button className="panel-dismiss-btn" onClick={onDismiss}>×</button>
        )}
        {!collapsed && !selectedClassId && <div />}
        <button className="panel-collapse-btn" onClick={toggle}>
          {collapsed ? "\u00AB" : "\u00BB"}
        </button>
      </div>
      {!collapsed && (
        <div className="panel-content" key={selectedClassId || `stats-${viewMode}-${date}`}>
          {selectedClassId ? (
            <ClassDetail
              classId={selectedClassId}
              onBack={onDismiss}
              onSessionExpired={onSessionExpired}
              exiting={false}
              skipAnimation
            />
          ) : (
            <PanelStats date={date} viewMode={viewMode} />
          )}
        </div>
      )}
    </div>
  );
}
